/**
 * `/account` — the "Profile" destination from the header's account menu: what
 * we hold about you, and the one thing you can currently change about it.
 *
 * Licences and billing belong to phase 3 (`docs/saas-architecture.md` §5) and
 * have no card here yet — an empty "Plan" row would read like a bug. Nothing
 * is invented: every value here comes from `GET /v1/me`.
 */

import type { CSSProperties } from "react";
import { Link } from "react-router-dom";

import { useSession } from "../../auth/AuthProvider";
import { SignOutButton } from "../../auth/SignOutButton";
import { AuthCard } from "./AuthCard";
import { ChangePasswordForm } from "./ChangePasswordForm";

export function Account() {
  const { user, api } = useSession();

  // `RequireAuth` guarantees a user by the time this renders. The check is for
  // the type system, and for whoever mounts this page somewhere else later.
  if (!user) return null;

  return (
    <div className="account">
      {/* A strip across the top of the card instead of the mark the other five
          auth pages wear. Decorative, and it carries no copy — it is the one
          place on a page of facts about your account where the world the rest
          of the site is set in gets a word in. `aria-hidden` because a still of
          a bear asleep by a vault door tells a screen reader nothing the page
          does not already say in words.

          The still is passed in rather than baked into `.auth__strip`: that
          class is a shape — full-bleed across the card, 5rem tall, scrimmed —
          and the second page to want one will want a different photograph. */}
      <AuthCard
        title="Your account"
        media={
          <div
            className="auth__strip"
            aria-hidden="true"
            style={{ "--strip-image": "url(/bear/strip-account.webp)" } as CSSProperties}
          />
        }
      >
        <dl className="account__details">
          <dt>Email</dt>
          <dd>
            {user.email}{" "}
            {user.emailVerified ? (
              <span className="badge badge--healthy">Confirmed</span>
            ) : (
              <span className="badge badge--stale">Not confirmed</span>
            )}
          </dd>

          <dt>Member since</dt>
          <dd>{memberSince(user.createdAt)}</dd>
        </dl>

        <p className="muted account__note">
          Your backups are encrypted on your own storage and never reach us, so there is nothing
          here to delete but the account itself. <Link to="/spec">The format is open</Link> — a
          backup outlives this account either way.
        </p>

        {/* Also reachable from the profile menu, but this page is where
            someone comes to deal with their account — being unable to leave
            from it means going back to the dashboard first to find the way. */}
        <p className="account__actions">
          <Link to="/dashboard">Back to your backups</Link>
          <SignOutButton />
        </p>
      </AuthCard>

      {/* No imagery at all on this one. It is a form you came here to fill in,
          the card above it already set the scene, and a second picture in the
          same narrow column would be the third thing competing for the top of
          it. */}
      <AuthCard title="Change your password" media={null}>
        <ChangePasswordForm api={api} />
      </AuthCard>
    </div>
  );
}

/**
 * The day, not the second. A join date is context, and a timestamp to the
 * second invites the reader to work out what it means.
 */
function memberSince(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}
